import React from 'react';
import { Mic, Loader2, Speech } from 'lucide-react';
import { LanguagePairSelector } from '@/components/shared/LanguagePairSelector';
import { Button } from '@/components/ui/button';
import { useMicrophoneState } from '@/contexts/MicrophoneContext';
import { FloatingArrow } from '@/components/ui/FloatingArrow';
import { MicrophonePermissionAlert } from '@/components/ui/MicrophonePermissionAlert';
import { useTranslation } from '@/contexts/I18nContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface HomePageProps {
  onStartTranslation: () => void;
  isLoading: boolean;
  disabled: boolean;
}

export const HomePage: React.FC<HomePageProps> = ({
  onStartTranslation,
  isLoading,
  disabled
}) => {
  const { t } = useTranslation();
  const { sourceLanguage, targetLanguage } = useLanguage();

  const {
    permissionStatus,
    availableDevices,
    selectedDeviceId,
    isLoading: micLoading,
    requestPermission,
    selectDevice,
  } = useMicrophoneState();

  // 控制警示UI的显示状态
  const [showPermissionAlert, setShowPermissionAlert] = React.useState(false);

  // 监听权限状态变化
  React.useEffect(() => {
    if (permissionStatus === 'denied') {
      setShowPermissionAlert(true);
    } else {
      setShowPermissionAlert(false);
    }
  }, [permissionStatus]);

  const handleMicrophoneChange = (deviceId: string) => {
    selectDevice(deviceId);
  };

  const handleRequestPermission = async () => {
    try {
      await requestPermission();
    } catch (error) {
      console.error('Failed to request microphone permission:', error);
    }
  };

  // 检查语言是否相同
  const isSameLanguage = sourceLanguage === targetLanguage;

  const canStart = selectedDeviceId && permissionStatus === 'granted' && !disabled && !isSameLanguage;

  return (
    <div className="flex flex-col h-full bg-linear-to-br from-background via-background to-muted/20">
      {/* 浮动箭头 - 只在需要权限时显示 */}
      {permissionStatus === 'prompt' && (
        <FloatingArrow
          targetSelector="#microphone-selector-area"
          text={t('audio.requestPermission')}
        />
      )}

      {/* 权限被禁用的警示UI */}
      <MicrophonePermissionAlert 
        isVisible={showPermissionAlert}
        onDismiss={() => setShowPermissionAlert(false)}
      />

      {/* Main content area - grows and scrolls */}
      <div className="flex-1 flex flex-col overflow-y-auto relative">
        {/* Content wrapper for centering, max-width, and padding */}
        <div className="container mx-auto px-4 py-8 max-w-4xl my-auto">
          {/* 头部标题 */}
          {/* <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Globe className="w-8 h-8 text-primary" />
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Mic className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Bilingual Translator
            </h1>
            <p className="text-xl text-muted-foreground">
              Real-time voice translation between languages
            </p>
          </div> */}

          {/* 语言验证错误提示 */}
          {isSameLanguage && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2 text-destructive">
                <span className="text-sm font-medium">
                  {t('translation.sameLanguageError')}
                </span>
              </div>
            </div>
          )}

          {/* 语言选择器 - 核心区域 */}
          <div id="language-selector-container" className="mb-12">
            <LanguagePairSelector />
          </div>

          {/* 开始翻译区域 */}
          {permissionStatus === 'granted' && (
            <div className="text-center space-y-6" data-fade>
              {/* 主要开始按钮 */}
              <div className="space-y-4">
                <Button
                  onClick={onStartTranslation}
                  disabled={!canStart || isLoading}
                  size="lg"
                  className="px-6 py-2 bg-black text-white rounded-lg font-bold transform hover:-translate-y-1 transition duration-400"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Speech className="w-6 h-6" />
                      {t('translation.startTranslation')}
                    </>
                  )}
                </Button>

              </div>

              {/* 状态提示 */}
              {!canStart && !isLoading && (
                <div className="text-sm text-muted-foreground">
                  {isSameLanguage
                    ? <span className="text-destructive">{t('translation.sameLanguageError')}</span>
                    : permissionStatus !== 'granted'
                    ? t('audio.permissionRequired')
                    : !selectedDeviceId
                    ? t('audio.selectMicrophone')
                    : t('common.loading')
                  }
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 麦克风选择 - MOVED HERE and positioned absolutely */}
      <div id="microphone-selector-area" className="absolute bottom-4 left-4 space-y-1 z-10 w-auto" data-fade>
        {permissionStatus === 'prompt' && (
          <div className="text-center">
            <Button
              onClick={handleRequestPermission}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              <Mic className="w-4 h-4" />
              {t('audio.requestPermission')}
            </Button>
          </div>
        )}

        {permissionStatus === 'granted' && availableDevices.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground block">
              {t('audio.selectMicrophone')}
            </label>
            <Select
              value={selectedDeviceId || ''}
              onValueChange={handleMicrophoneChange}
              disabled={micLoading}
            >
              <SelectTrigger className="h-7 text-xs px-2 w-auto">
                <SelectValue placeholder={t('audio.selectMicrophone')}>
                  <div className="flex items-center gap-2">
                    <Mic className="w-3 h-3" />
                    <span className="truncate text-xs">
                      {selectedDeviceId
                        ? availableDevices.find(d => d.deviceId === selectedDeviceId)?.label || t('audio.noMicrophoneFound')
                        : t('audio.selectMicrophone')
                      }
                    </span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableDevices
                  .filter((device) => device.deviceId && device.deviceId.trim() !== '')
                  .map((device) => (
                  <SelectItem key={device.deviceId} value={device.deviceId}>
                    <div className="flex items-center gap-2">
                      <Mic className="w-3 h-3" />
                      <span className="truncate text-xs">{device.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {permissionStatus === 'denied' && (
          <div className="text-center">
            <p className="text-xs text-destructive mb-2">
              {t('audio.microphonePermissionDenied')}
            </p>
            <Button
              onClick={handleRequestPermission}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              {t('common.retry')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}; 